import {
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { ArrowLeft2, ArrowRight2, Calendar } from 'iconsax-reactjs';
import { formatPlainDate, isPlainDate, todayInZone, useTimeZone } from '@/lib/time';
import { useFieldProps } from '../Form/fieldContext';
import type { ControlSize } from '../Form/TextInput';
import { cx } from '../internal/cx';
import { Popover } from '../Popover/Popover';
import {
  addDays,
  addMonths,
  clampDate,
  datePlaceholder,
  dayOfMonth,
  endOfWeek,
  formatDateInput,
  fullDateLabel,
  isOutOfRange,
  monthGrid,
  monthLabel,
  parseDateInput,
  sameMonth,
  startOfWeek,
  weekdayLabels,
} from './calendar';
import formStyles from '../Form/Form.module.css';
import styles from './DateTime.module.css';

export interface DatePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'type' | 'size'
> {
  /** Plain calendar date "YYYY-MM-DD", or "" when empty. Calendar dates have no time zone. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest selectable date ("YYYY-MM-DD"). */
  min?: string;
  /** Latest selectable date ("YYYY-MM-DD"). */
  max?: string;
  /** 0 = Sunday (US default), 1 = Monday. */
  weekStartsOn?: 0 | 1;
  size?: ControlSize;
  invalid?: boolean;
  /** Accessible name of the calendar button and dialog. */
  calendarLabel?: string;
  ref?: Ref<HTMLInputElement>;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  weekStartsOn = 0,
  size = 'md',
  invalid,
  calendarLabel = 'Choose date',
  id,
  required,
  disabled,
  readOnly,
  placeholder,
  className,
  onBlur,
  onKeyDown,
  'aria-describedby': ariaDescribedBy,
  ref,
  ...rest
}: DatePickerProps) {
  const { timeZone, locale } = useTimeZone();
  const errorId = useId();
  const monthHeadingId = useId();
  const [draft, setDraft] = useState(() => formatDateInput(value, locale));
  const [syncedValue, setSyncedValue] = useState(value);
  const [parseError, setParseError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [focusedDate, setFocusedDate] = useState(() => value || todayInZone(timeZone));
  const anchorRef = useRef<HTMLDivElement>(null);
  const focusedDayRef = useRef<HTMLButtonElement>(null);
  const moveGridFocus = useRef(false);

  // Keep the text in sync when the value changes from outside.
  if (value !== syncedValue) {
    setSyncedValue(value);
    setDraft(formatDateInput(value, locale));
    setParseError(null);
  }

  const field = useFieldProps({
    id,
    'aria-describedby': cx(parseError ? errorId : undefined, ariaDescribedBy) || undefined,
    invalid: invalid || Boolean(parseError) || undefined,
    required,
    disabled,
  });

  useEffect(() => {
    if (!open || !moveGridFocus.current) return;
    moveGridFocus.current = false;
    focusedDayRef.current?.focus();
  }, [open, focusedDate]);

  const today = todayInZone(timeZone);

  function rangeMessage(): string {
    if (min && max) {
      return `Enter a date between ${formatPlainDate(min, { locale })} and ${formatPlainDate(max, { locale })}.`;
    }
    if (min) return `Enter a date on or after ${formatPlainDate(min, { locale })}.`;
    return `Enter a date on or before ${formatPlainDate(max ?? '', { locale })}.`;
  }

  function commit(text: string) {
    const trimmed = text.trim();
    if (!trimmed) {
      setParseError(null);
      if (value !== '') onChange('');
      return;
    }
    const parsed = parseDateInput(trimmed, locale);
    if (!parsed) {
      setParseError(`Enter a valid date as ${datePlaceholder(locale)}.`);
      return;
    }
    if (isOutOfRange(parsed, min, max)) {
      setParseError(rangeMessage());
      return;
    }
    setParseError(null);
    setDraft(formatDateInput(parsed, locale));
    if (parsed !== value) onChange(parsed);
  }

  function openCalendar() {
    if (disabled || readOnly) return;
    const base = parseDateInput(draft, locale) ?? (value && isPlainDate(value) ? value : today);
    setFocusedDate(clampDate(base, min, max));
    setOpen(true);
  }

  function select(date: string) {
    if (isOutOfRange(date, min, max)) return;
    setParseError(null);
    setDraft(formatDateInput(date, locale));
    setOpen(false);
    if (date !== value) onChange(date);
  }

  function onGridKeyDown(event: KeyboardEvent<HTMLTableElement>) {
    let next: string | null = null;
    switch (event.key) {
      case 'ArrowLeft':
        next = addDays(focusedDate, -1);
        break;
      case 'ArrowRight':
        next = addDays(focusedDate, 1);
        break;
      case 'ArrowUp':
        next = addDays(focusedDate, -7);
        break;
      case 'ArrowDown':
        next = addDays(focusedDate, 7);
        break;
      case 'PageUp':
        next = addMonths(focusedDate, event.shiftKey ? -12 : -1);
        break;
      case 'PageDown':
        next = addMonths(focusedDate, event.shiftKey ? 12 : 1);
        break;
      case 'Home':
        next = startOfWeek(focusedDate, weekStartsOn);
        break;
      case 'End':
        next = endOfWeek(focusedDate, weekStartsOn);
        break;
      default:
        return;
    }
    event.preventDefault();
    moveGridFocus.current = true;
    setFocusedDate(clampDate(next, min, max));
  }

  const weeks = open ? monthGrid(focusedDate, weekStartsOn) : [];
  const weekdays = open ? weekdayLabels(weekStartsOn, locale) : [];
  const prevMonthDisabled = Boolean(
    min && isOutOfRange(addDays(focusedDate.slice(0, 8) + '01', -1), min),
  );
  const nextMonthDisabled = Boolean(
    max && isOutOfRange(addMonths(focusedDate.slice(0, 8) + '01', 1), undefined, max),
  );
  const buttonLabel =
    value && isPlainDate(value)
      ? `${calendarLabel}, selected date is ${fullDateLabel(value, locale)}`
      : calendarLabel;

  return (
    <div className={styles.pickerRoot}>
      <div
        ref={anchorRef}
        className={cx(
          formStyles.control,
          formStyles[`control-${size}`],
          field.invalid && formStyles.controlInvalid,
          field.disabled && formStyles.controlDisabled,
          readOnly && formStyles.controlReadOnly,
        )}
      >
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          id={field.id}
          className={cx(formStyles.input, styles.dateInput, className)}
          value={draft}
          placeholder={placeholder ?? datePlaceholder(locale)}
          aria-describedby={field.describedBy}
          aria-invalid={field.invalid || undefined}
          required={field.required || undefined}
          disabled={field.disabled || undefined}
          readOnly={readOnly}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={(event) => {
            onBlur?.(event);
            commit(event.target.value);
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented) return;
            if (event.key === 'Enter') commit(event.currentTarget.value);
            if (event.key === 'ArrowDown' && event.altKey) {
              event.preventDefault();
              openCalendar();
            }
          }}
          {...rest}
        />
        <span className={formStyles.trailing}>
          <button
            type="button"
            className={formStyles.inlineButton}
            aria-label={buttonLabel}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={field.disabled || readOnly}
            onClick={() => (open ? setOpen(false) : openCalendar())}
          >
            <Calendar size={18} color="currentColor" aria-hidden="true" />
          </button>
        </span>
      </div>
      {parseError && (
        <p id={errorId} className={formStyles.error}>
          {parseError}
        </p>
      )}
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        role="dialog"
        aria-label={calendarLabel}
        trapFocus
        modal
        align="start"
        initialFocusRef={focusedDayRef}
        className={styles.calendar}
      >
        <div className={styles.calendarHeader}>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Previous month"
            disabled={prevMonthDisabled}
            onClick={() => setFocusedDate(clampDate(addMonths(focusedDate, -1), min, max))}
          >
            <ArrowLeft2 size={16} color="currentColor" aria-hidden="true" />
          </button>
          <p id={monthHeadingId} className={styles.monthLabel} aria-live="polite">
            {open ? monthLabel(focusedDate, locale) : ''}
          </p>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Next month"
            disabled={nextMonthDisabled}
            onClick={() => setFocusedDate(clampDate(addMonths(focusedDate, 1), min, max))}
          >
            <ArrowRight2 size={16} color="currentColor" aria-hidden="true" />
          </button>
        </div>
        <table
          role="grid"
          aria-labelledby={monthHeadingId}
          className={styles.grid}
          onKeyDown={onGridKeyDown}
        >
          <thead>
            <tr>
              {weekdays.map((day) => (
                <th key={day.long} scope="col" abbr={day.long} className={styles.weekday}>
                  {day.short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week[0]}>
                {week.map((date) => {
                  const isSelected = date === value;
                  const isFocused = date === focusedDate;
                  const outside = !sameMonth(date, focusedDate);
                  const unavailable = isOutOfRange(date, min, max);
                  return (
                    <td
                      key={date}
                      role="gridcell"
                      aria-selected={isSelected}
                      className={styles.dayCell}
                    >
                      <button
                        ref={isFocused ? focusedDayRef : undefined}
                        type="button"
                        tabIndex={isFocused ? 0 : -1}
                        data-date={date}
                        aria-label={fullDateLabel(date, locale)}
                        aria-current={date === today ? 'date' : undefined}
                        aria-disabled={unavailable || undefined}
                        className={cx(
                          styles.day,
                          outside && styles.dayOutside,
                          isSelected && styles.daySelected,
                          date === today && styles.dayToday,
                        )}
                        onClick={() => {
                          setFocusedDate(date);
                          select(date);
                        }}
                      >
                        {dayOfMonth(date)}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className={styles.calendarFooter}>
          <button
            type="button"
            className={styles.footerButton}
            disabled={isOutOfRange(today, min, max)}
            onClick={() => select(today)}
          >
            Today
          </button>
          {value && !field.required && (
            <button
              type="button"
              className={styles.footerButton}
              onClick={() => {
                setOpen(false);
                setDraft('');
                onChange('');
              }}
            >
              Clear
            </button>
          )}
        </div>
      </Popover>
    </div>
  );
}
